import { persistentAtom, persistentMap } from '@nanostores/persistent';
import { atom, computed, map } from 'nanostores';
import { ulid } from 'ulid';

import { clamp } from '@iconik-io/core/src/clamp';
import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { roundToDecimals } from '@iconik-io/core/src/round_to_decimals';
import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import type { DeepPartial } from '@iconik-io/core/src/types.js';

import type { SyncClient } from '../../sync/client/sync_client.js';
import { syncAtom } from '../../sync/sync_atom.js';
import { syncMap } from '../../sync/sync_map.js';
import type {
  Annotation,
  AnnotationElement,
  AnnotationOperation,
  AnnotationOptions,
} from '../../types.js';
import { deepMergePartial } from '../../utils/deep_merge_partials.js';
import { booleanEncoder, genericEncoder } from './persistent_encoding.js';

const logger = DebugLogger.getInstance('unified_player_annotations');

const MAX_LINE_WIDTH = 32;
const AVAILABLE_LINE_WIDTHS = [2, 6, 8, 12, 16, 20, 24];

const isAnnotationElement = (
  element: unknown,
): element is AnnotationElement => {
  return (
    typeof element === 'object' &&
    element !== null &&
    'type' in element &&
    typeof element.type === 'string' &&
    'points' in element &&
    Array.isArray(element.points) &&
    'options' in element &&
    typeof element.options === 'object' &&
    element.options !== null
  );
};

// Remove invalid annotation elements from the annotation
function sanitizeAnnotation(annotation: DeepPartial<Annotation>): Annotation {
  const result: Annotation = {};
  // Shallowly validate each element in the annotation
  for (const [id, element] of Object.entries(annotation)) {
    if (!isAnnotationElement(element)) {
      continue;
    }

    result[id] = element;
  }

  return result;
}

export function createAnnotationsStore(syncClient: SyncClient) {
  const $settings = persistentMap<AnnotationOptions>(
    'UP_annotationSettings',
    {
      color: '#ffffff',
      width: 8,
      opacity: 1,
    },
    genericEncoder,
  );

  const $isContextualAnnotationVisible = syncAtom<boolean>(
    'isContextualAnnotationVisible',
    true,
    {
      syncClient,
    },
  );

  const $drawingDisabled = atom<boolean>(false);

  const $drawingToolsDisabled = atom<boolean>(false);

  const $undoHistory = atom<AnnotationOperation[]>([]);

  const $redoHistory = atom<AnnotationOperation[]>([]);

  /**
   * $contextualAnnotation is an ephemeral layer driven by the current segment selection.
   */
  const $contextualAnnotation = map<Annotation>({});

  function setContextualAnnotation(annotation: Annotation) {
    $contextualAnnotation.set(annotation);
  }

  /**
   * $aggregatedDraftAnnotation is a merged, real-time union of all users’ drafts.
   */
  const $aggregatedDraftAnnotation = syncMap<Annotation>(
    'aggregatedDraftAnnotation',
    {},
    { syncClient },
  );

  /**
   * $personalDraftAnnotation each participant’s private,
   * unsaved annotations that propagate into the $aggregatedDraftAnnotation
   */
  const $personalDraftAnnotation = map<Annotation>();

  const $selectedAnnotationElementIds = atom<string[]>([]);

  const $selectedAnnotationColor = computed(
    [$selectedAnnotationElementIds, $personalDraftAnnotation],
    (selectedIds, currentAnnotation) => {
      if (selectedIds.length !== 1) {
        return undefined;
      }
      const firstSelectedElement = currentAnnotation[selectedIds[0]];
      return firstSelectedElement
        ? firstSelectedElement.options.color
        : undefined;
    },
  );

  const $selectedAnnotationLineWidth = computed(
    [$selectedAnnotationElementIds, $personalDraftAnnotation],
    (selectedIds, currentAnnotation) => {
      if (selectedIds.length !== 1) {
        return undefined;
      }
      const firstSelectedElement = currentAnnotation[selectedIds[0]];
      return firstSelectedElement
        ? firstSelectedElement.options.width * 1000
        : undefined;
    },
  );

  $personalDraftAnnotation.listen((annotation, oldAnnotation) => {
    for (const id of Object.keys(oldAnnotation)) {
      if (!annotation[id]) {
        $aggregatedDraftAnnotation.setKey(id, undefined);
      }
    }
    for (const [id, value] of Object.entries(annotation)) {
      $aggregatedDraftAnnotation.setKey(id, value);
    }
  });

  function setPersonalDraftAnnotation(annotation: Annotation) {
    logger.debug('Setting personal draft annotation', annotation);
    $personalDraftAnnotation.set(annotation);
  }

  /**
   * Temporary store for laser annotations. Each element is removed after
   * a short time once finished.
   */
  const $laserAnnotation = syncMap<Annotation>(
    'laserAnnotation',
    {},
    { syncClient },
  );

  $laserAnnotation.listen((annotation) => {
    if (!annotation) {
      return;
    }

    for (const [key, value] of Object.entries(annotation)) {
      if (value.finished) {
        setTimeout(() => removeLaserStroke(key), 1000);
      }
    }
  });

  function addLaserStroke(id: string, annotationElement: AnnotationElement) {
    $laserAnnotation.setKey(id, annotationElement);
  }

  function removeLaserStroke(id: string) {
    $laserAnnotation.setKey(id, undefined);
  }

  function clearPersonalDraftAnnotation(addHistoryState = false) {
    logger.debug(
      'Clearing personal draft annotation, addHistoryState:',
      addHistoryState,
    );
    const currentAnnotation = $personalDraftAnnotation.get();

    for (const id of Object.keys(currentAnnotation)) {
      $aggregatedDraftAnnotation.setKey(id, undefined);
    }
    setPersonalDraftAnnotation({});

    if (!addHistoryState) {
      clearHistory();
      return;
    }
    addToUndoHistory(currentAnnotation);
  }

  function clearHistory() {
    $undoHistory.set([]);
    $redoHistory.set([]);
  }

  function updatePersonalDraftElements(annotation: Annotation) {
    logger.debug('Updating personal draft elements', annotation);
    const personalDraftAnnotation = $personalDraftAnnotation.get();
    const undoOperation: AnnotationOperation = {};
    for (const [elementId, element] of Object.entries(annotation)) {
      $personalDraftAnnotation.setKey(elementId, element);
      const existingElement = personalDraftAnnotation[elementId];
      if (!existingElement) {
        undoOperation[elementId] = undefined; // The undo operation for an add is to remove the element
        continue;
      }

      if (existingElement.finished === false) {
        continue; // Don't add to history if the element is not finished
      }

      // Updates to existing elements can either be text changes or point changes.
      if (element.options.text !== existingElement.options.text) {
        undoOperation[elementId] = {
          options: {
            text: existingElement.options.text,
          },
        };
        continue;
      }
      undoOperation[elementId] = {
        points: existingElement.points,
      };
    }

    if (Object.keys(undoOperation).length > 0) {
      addToUndoHistory(undoOperation);
    }
  }

  function removePersonalDraftElements(elementIds: string[]): Annotation {
    logger.debug('Removing personal draft elements', elementIds);
    const removedAnnotationElements: Annotation = {};
    const personalDraftAnnotation = $personalDraftAnnotation.get();
    for (const elementId of elementIds) {
      removedAnnotationElements[elementId] = personalDraftAnnotation[elementId];
      $personalDraftAnnotation.setKey(elementId, undefined);
    }

    return removedAnnotationElements;
  }

  function setIsContextualAnnotationVisible(value: boolean) {
    const currentValue = $isContextualAnnotationVisible.get();
    if (currentValue === value) {
      return;
    }
    logger.debug(
      'Setting isContextualAnnotationVisible to',
      value,
      'current value:',
      currentValue,
    );
    $isContextualAnnotationVisible.set(value);
  }

  function setStrokeColor(color: string) {
    if ($selectedAnnotationElementIds.get().length !== 0) {
      updateSelectedAnnotationColor(color);
    }
    $settings.setKey('color', color);
  }

  function incrementLineWidth(byThisAmount: number) {
    setLineWidth($settings.get().width + byThisAmount);
  }

  function increaseLineWidth() {
    const nextLineWidth =
      AVAILABLE_LINE_WIDTHS[
        AVAILABLE_LINE_WIDTHS.indexOf($settings.get().width) + 1
      ];
    if (nextLineWidth !== undefined) {
      setLineWidth(nextLineWidth);
    }
  }

  function decreaseLineWidth() {
    const prevLineWidth =
      AVAILABLE_LINE_WIDTHS[
        AVAILABLE_LINE_WIDTHS.indexOf($settings.get().width) - 1
      ];
    if (prevLineWidth !== undefined) {
      setLineWidth(prevLineWidth);
    }
  }

  function setLineWidth(lineWidth: number) {
    if (lineWidth > 0 && lineWidth <= MAX_LINE_WIDTH) {
      if ($selectedAnnotationElementIds.get().length > 0) {
        updateSelectedAnnotationLineWidth(lineWidth);
      }
      $settings.setKey('width', lineWidth);
    }
  }

  function getAvailableLineWidths() {
    return AVAILABLE_LINE_WIDTHS;
  }

  const $redoAvailable = computed(
    [$redoHistory],
    (redoHistory) => redoHistory.length > 0,
  );
  const $undoAvailable = computed(
    [$undoHistory],
    (undoHistory) => undoHistory.length > 0,
  );

  function addToUndoHistory(operation: AnnotationOperation) {
    $undoHistory.set([operation, ...$undoHistory.get()]);
  }

  function addToRedoHistory(operation: AnnotationOperation) {
    $redoHistory.set([operation, ...$redoHistory.get()]);
  }

  function shiftUndoHistory() {
    const undoHistory = [...$undoHistory.get()];
    const undoOperation = undoHistory.shift();

    if (undoOperation === undefined) {
      return;
    }

    $undoHistory.set(undoHistory);

    return undoOperation;
  }

  function shiftRedoHistory() {
    const redoHistory = [...$redoHistory.get()];
    const redoOperation = redoHistory.shift();

    if (redoOperation === undefined) {
      return;
    }

    $redoHistory.set(redoHistory);
    return redoOperation;
  }

  function undo() {
    const undoOperation = shiftUndoHistory();
    if (!undoOperation) {
      return;
    }
    const annotation = { ...$personalDraftAnnotation.get() };
    const [newAnnotation, redoOperation] = deepMergePartial(
      annotation,
      undoOperation,
    );
    const sanitizedAnnotation = sanitizeAnnotation(newAnnotation);

    setPersonalDraftAnnotation(sanitizedAnnotation);
    addToRedoHistory(redoOperation);
  }

  function redo() {
    const redoOperation = shiftRedoHistory();
    if (!redoOperation) {
      return;
    }
    const annotation = { ...$personalDraftAnnotation.get() };
    const [newAnnotation, undoOperation] = deepMergePartial(
      annotation,
      redoOperation,
    );
    const sanitizedAnnotation = sanitizeAnnotation(newAnnotation);
    setPersonalDraftAnnotation(sanitizedAnnotation);
    addToUndoHistory(undoOperation);
  }

  function setSelectedAnnotationElementIds(
    annotationElementIds: string[],
  ): void {
    $selectedAnnotationElementIds.set(annotationElementIds);
  }

  function deleteSelectedAnnotationElement() {
    const removedAnnotationElements = removePersonalDraftElements(
      $selectedAnnotationElementIds.get(),
    );
    addToUndoHistory(removedAnnotationElements);

    setSelectedAnnotationElementIds([]);
  }

  function serializeSelectedAnnotationElements(): string {
    const currentAnnotation = $personalDraftAnnotation.get();
    const selectedIds = $selectedAnnotationElementIds.get();
    const selectedElements = selectedIds
      .map((id) => currentAnnotation[id])
      .filter(truthyPredicate);
    return JSON.stringify(selectedElements);
  }

  function deserializeAndAddAnnotationElements(serializedData: string): void {
    try {
      const parsedData: unknown = JSON.parse(serializedData);
      const validElements = Array.isArray(parsedData)
        ? parsedData.filter(isAnnotationElement)
        : [];

      if (validElements.length === 0) {
        return;
      }

      const newElements: Annotation = {};
      validElements.forEach((element) => {
        const newId = ulid();
        newElements[newId] = element;
      });
      const currentAnnotation = $personalDraftAnnotation.get();
      const undoOperation: AnnotationOperation = {};
      Object.keys(newElements).forEach((annotationKey) => {
        undoOperation[annotationKey] = undefined; // The undo operation for an add is to remove the element
      });
      addToUndoHistory(undoOperation);
      setPersonalDraftAnnotation({ ...currentAnnotation, ...newElements });
    } catch {}
  }

  function increaseOpacity() {
    const nextOpacity = roundToDecimals($settings.get().opacity + 0.1, 1);
    if (nextOpacity !== undefined) {
      setOpacity(nextOpacity);
    }
  }

  function decreaseOpacity() {
    const nextOpacity = roundToDecimals($settings.get().opacity - 0.1, 1);
    if (nextOpacity !== undefined) {
      setOpacity(nextOpacity);
    }
  }

  function setOpacity(opacity: number) {
    if ($selectedAnnotationElementIds.get().length !== 0) {
      updateSelectedAnnotationOpacity(opacity);
    }
    $settings.setKey('opacity', clamp(opacity, 0.1, 1));
  }

  function disableDrawing(state: boolean) {
    $drawingDisabled.set(state);
  }

  function disableDrawingTools(state: boolean) {
    $drawingToolsDisabled.set(state);
  }

  function updateSelectedAnnotationColor(color: string) {
    updateSelectedAnnotationOption('color', color);
  }

  function updateSelectedAnnotationOpacity(opacity: number) {
    updateSelectedAnnotationOption('opacity', opacity);
  }

  function updateSelectedAnnotationLineWidth(width: number) {
    updateSelectedAnnotationOption('width', width / 1000);
  }

  function updateSelectedAnnotationOption<
    T extends keyof AnnotationElement['options'],
  >(optionKey: T, value: AnnotationElement['options'][T]) {
    const selectedIds = $selectedAnnotationElementIds.get();
    const personalDraftAnnotation = $personalDraftAnnotation.get();
    const updatedAnnotation = { ...personalDraftAnnotation };
    const undoOperation: AnnotationOperation = {};
    selectedIds.forEach((id) => {
      if (updatedAnnotation[id]) {
        undoOperation[id] = {
          options: {
            [optionKey]: personalDraftAnnotation[id].options[optionKey],
          },
        };
        updatedAnnotation[id] = {
          ...updatedAnnotation[id],
          options: {
            ...updatedAnnotation[id].options,
            [optionKey]: value,
          },
        };
      }
    });

    setPersonalDraftAnnotation(updatedAnnotation);
  }

  const $showTracingLayer = persistentAtom<boolean>(
    'UP_showTracingLayer',
    false,
    booleanEncoder,
  );

  function setShowTracingLayer(value: boolean) {
    $showTracingLayer.set(value);
  }

  return {
    $contextualAnnotation,
    $personalDraftAnnotation,
    $aggregatedDraftAnnotation,
    $settings,
    $laserAnnotation,
    $drawingDisabled,
    $drawingToolsDisabled,
    $isContextualAnnotationVisible,
    $undoAvailable,
    $redoAvailable,
    $selectedAnnotationElementIds,
    $selectedAnnotationColor,
    $selectedAnnotationLineWidth,
    setContextualAnnotation,
    setIsContextualAnnotationVisible,
    setPersonalDraftAnnotation,
    clearPersonalDraftAnnotation,
    updatePersonalDraftElements,
    removePersonalDraftElements,
    setStrokeColor,
    incrementLineWidth,
    increaseLineWidth,
    decreaseLineWidth,
    setLineWidth,
    setSelectedAnnotationElementIds,
    getAvailableLineWidths,
    undo,
    redo,
    deleteSelectedAnnotationElement,
    serializeSelectedAnnotationElements,
    deserializeAndAddAnnotationElements,
    increaseOpacity,
    decreaseOpacity,
    setOpacity,
    disableDrawing,
    disableDrawingTools,
    addLaserStroke,
    $showTracingLayer,
    setShowTracingLayer,
  };
}
